import { z } from 'zod';
import { FilterGroupSchema, GroupLogicSchema, ME_TOKEN, type FilterCondition } from '../flow/filter';
import { safeParseJson } from '../parse-json';
import type { FilterFieldCatalogEntry } from '../flow/filter-fields';
import { isValidOperatorForType, NO_VALUE_OPERATORS, validateOperatorValueShape } from '../flow/filter-operators';
import { loopPriority, loopResponsibility, loopSourceDate } from '../home/loop-fields';
import { assigneeLabel, loopAssignee, type OpenLoop } from './wire';

/**
 * Filters over open loops, for the Home board's Open Loops column and the Open Loops page.
 *
 * Both surfaces use the ledger toolbar's Filters control, so a filter is the same
 * `FilterGroup[]` + `GroupLogic` the tables send. The tables compile that to SQL
 * (`condition-to-sql.ts`); loops are not rows in a table, so this module evaluates it
 * over loop objects instead, with the same semantics: text, picklist and person compare
 * case-insensitively, every negation keeps rows with no value, and a group with no
 * usable condition is skipped. Shared, so the board (server) and the page (browser)
 * agree on what a filter keeps.
 */

export const LOOP_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  {
    key: 'assignee',
    label: 'Assignee',
    group: 'Loop',
    type: 'person',
    // `{me}` cannot sit inside an array value, so the array operators are left out: "is Me" is the case that matters.
    allowedOperators: ['equals', 'not_equals', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'responsibility',
    label: 'Next steps owner',
    group: 'Loop',
    type: 'picklist',
    values: [
      { value: 'us', label: 'Us' },
      { value: 'them', label: 'Prospect' },
      { value: 'both', label: 'Both' },
    ],
    allowedOperators: ['equals', 'not_equals', 'in_any_of', 'not_in_any_of', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'priority',
    label: 'Priority',
    group: 'Loop',
    type: 'picklist',
    values: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ],
    allowedOperators: ['equals', 'not_equals', 'in_any_of', 'not_in_any_of', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'account',
    label: 'Account',
    group: 'Loop',
    type: 'text',
    allowedOperators: ['equals', 'not_equals', 'contains', 'not_contains', 'in_any_of', 'not_in_any_of'],
  },
  {
    key: 'last_discussed',
    label: 'Last discussed',
    group: 'Loop',
    type: 'date',
    allowedOperators: ['equals', 'gt', 'gte', 'lt', 'in_last_days', 'not_in_last_days', 'between', 'is_empty', 'is_not_empty'],
  },
];

export const LoopFilterSchema = z.object({
  groups: z.array(FilterGroupSchema),
  logic: GroupLogicSchema,
});
export type LoopFilter = z.infer<typeof LoopFilterSchema>;

/** No conditions: every loop. */
export const ALL_LOOPS_FILTER: LoopFilter = { groups: [], logic: 'AND' };

/**
 * "Mine": loops assigned to the reader, plus every loop waiting on the prospect
 * (`responsibility: 'them'`), whoever at Clay is chasing it. One labelled OR group, so
 * the rep can add conditions beside it and the chip still reads "Mine".
 */
export const MINE_LOOP_FILTER: LoopFilter = {
  groups: [
    {
      id: 'mine',
      label: 'Mine',
      logic: 'OR',
      conditions: [
        { id: 'mine-assignee', field: 'assignee', operator: 'equals', value: ME_TOKEN },
        { id: 'mine-court', field: 'responsibility', operator: 'equals', value: 'them' },
      ],
    },
  ],
  logic: 'AND',
};

/** The two preset scopes, as the `my_day` tool's `loops` input names them. */
export const LoopScopeSchema = z.enum(['mine', 'all']);
export type LoopScope = z.infer<typeof LoopScopeSchema>;

/** The filter a `mine` / `all` scope stands for: the `my_day` tool's `loops` input. */
export function loopFilterForScope(scope: LoopScope): LoopFilter {
  return scope === 'mine' ? MINE_LOOP_FILTER : ALL_LOOPS_FILTER;
}

export function isAllLoopsFilter(filter: LoopFilter): boolean {
  return filter.groups.every((g) => g.conditions.length === 0);
}

/** True when `filter` is the Mine preset, ignoring ids and labels. */
export function isMineLoopFilter(filter: LoopFilter): boolean {
  const active = filter.groups.filter((g) => g.conditions.length > 0);
  const [mine] = MINE_LOOP_FILTER.groups;
  if (active.length !== 1 || active[0].logic !== mine.logic) return false;
  const shape = (c: FilterCondition) => `${c.field}|${c.operator}|${JSON.stringify(c.value)}`;
  const want = mine.conditions.map(shape).sort();
  const got = active[0].conditions.map(shape).sort();
  return want.length === got.length && want.every((s, i) => s === got[i]);
}

const FIELD_BY_KEY = new Map(LOOP_FILTER_FIELDS.map((f) => [f.key, f]));

/**
 * The first problem with `filter`, or null when every condition names a loop field, an
 * operator that field allows, and a value of the right shape. The API answers a 400
 * with it, so a bad filter is never silently half-applied.
 */
export function validateLoopFilter(filter: LoopFilter): string | null {
  for (const group of filter.groups) {
    for (const cond of group.conditions) {
      const field = FIELD_BY_KEY.get(cond.field);
      if (!field) return `Unknown loop filter field "${cond.field}"`;
      if (!isValidOperatorForType(field.type, cond.operator) || (field.allowedOperators && !field.allowedOperators.includes(cond.operator))) {
        return `Operator "${cond.operator}" is not allowed for "${cond.field}"`;
      }
      const values = field.values?.map((v) => (typeof v === 'string' ? v : v.value));
      const shapeError = validateOperatorValueShape(field.type, cond.operator, cond.value, values);
      if (shapeError) return `"${cond.field}": ${shapeError}`;
    }
  }
  return null;
}

/** A `LoopFilter` that also passes {@link validateLoopFilter}: the one schema every boundary parses a filter with. */
export const ValidLoopFilterSchema = LoopFilterSchema.superRefine((filter, ctx) => {
  const error = validateLoopFilter(filter);
  if (error) ctx.addIssue({ code: 'custom', message: error });
});

/** What kind of loop filter is applied, for analytics and the Claude handoff: never its contents. */
export function loopFilterKind(filter: LoopFilter | undefined): 'mine' | 'all' | 'custom' {
  if (!filter || isAllLoopsFilter(filter)) return 'all';
  return isMineLoopFilter(filter) ? 'mine' : 'custom';
}

/** The loop and the account it sits on: what a loop filter can read. */
export interface FilterableLoop {
  loop: OpenLoop;
  accountName: string | null;
}

export interface LoopFilterContext {
  /** The reader's addresses, lowercased, for `{me}`. Empty matches no assignee. */
  myEmails: ReadonlySet<string>;
  /** Today as `YYYY-MM-DD` (UTC), for the relative date operators. */
  today: string;
}

/** Whether `row` passes `filter`. An empty filter keeps everything. */
export function loopMatchesFilter(row: FilterableLoop, filter: LoopFilter, ctx: LoopFilterContext): boolean {
  const results: boolean[] = [];
  for (const group of filter.groups) {
    const conds = group.conditions
      .map((c) => evaluateCondition(row, c, ctx))
      .filter((r): r is boolean => r !== null);
    if (conds.length === 0) continue;
    results.push(group.logic === 'AND' ? conds.every(Boolean) : conds.some(Boolean));
  }
  if (results.length === 0) return true;
  return filter.logic === 'AND' ? results.every(Boolean) : results.some(Boolean);
}

/** A predicate for `filter`, or null when it filters nothing (so callers can skip the pass). */
export function loopFilterPredicate(filter: LoopFilter, ctx: LoopFilterContext): ((row: FilterableLoop) => boolean) | null {
  if (isAllLoopsFilter(filter)) return null;
  return (row) => loopMatchesFilter(row, filter, ctx);
}

const lower = (s: string) => s.trim().toLowerCase();

/** true / false, or null when the condition cannot be evaluated — dropped, as `conditionToSql` drops it. */
function evaluateCondition(row: FilterableLoop, cond: FilterCondition, ctx: LoopFilterContext): boolean | null {
  const field = FIELD_BY_KEY.get(cond.field);
  if (!field) return null;
  if (field.type === 'date') return evaluateDate(loopSourceDate(row.loop), cond, ctx.today);
  if (cond.field === 'assignee') return evaluateAssignee(row.loop, cond, ctx);

  const actual = cond.field === 'account' ? row.accountName : cond.field === 'priority' ? loopPriority(row.loop) : loopResponsibility(row.loop);
  return evaluateText(actual === null || actual.trim() === '' ? null : [lower(actual)], cond);
}

/**
 * Text semantics over the values a row offers for a field (a person offers both their
 * name and their address). `null` means the row has no value.
 */
function evaluateText(actual: string[] | null, cond: FilterCondition): boolean | null {
  const { operator, value } = cond;
  if (operator === 'is_empty') return actual === null;
  if (operator === 'is_not_empty') return actual !== null;
  if (value === null) return null;
  const wanted = (Array.isArray(value) ? value : [String(value)]).map(lower);
  const has = (w: string) => actual !== null && actual.includes(w);
  const contains = (w: string) => actual !== null && actual.some((a) => a.includes(w));
  switch (operator) {
    case 'equals': return has(wanted[0]);
    case 'not_equals': return !has(wanted[0]);
    case 'contains': return contains(wanted[0]);
    case 'not_contains': return !contains(wanted[0]);
    case 'in_any_of': return wanted.some(has);
    case 'not_in_any_of': return !wanted.some(has);
    default: return null;
  }
}

/**
 * `{me}` is the reader's address; any other value is a name, matched against the assignee's name or address.
 * Emptiness is decided first: a leftover `{me}` on "is empty" / "is not empty" must still filter, not drop the condition.
 */
function evaluateAssignee(loop: OpenLoop, cond: FilterCondition, ctx: LoopFilterContext): boolean | null {
  const assignee = loopAssignee(loop);
  const email = assignee?.email ? lower(assignee.email) : null;
  const label = assigneeLabel(assignee);
  const names = [label, assignee?.name ?? null, email].filter((s): s is string => !!s && s.trim() !== '').map(lower);
  const actual = names.length > 0 ? names : null;
  if (NO_VALUE_OPERATORS.has(cond.operator) || cond.value !== ME_TOKEN) return evaluateText(actual, cond);
  const mine = email !== null && ctx.myEmails.has(email);
  if (cond.operator === 'equals') return mine;
  if (cond.operator === 'not_equals') return !mine;
  return null;
}

function evaluateDate(actual: string | null, cond: FilterCondition, today: string): boolean | null {
  const { operator, value } = cond;
  if (operator === 'is_empty') return actual === null;
  if (operator === 'is_not_empty') return actual !== null;
  if (NO_VALUE_OPERATORS.has(operator)) return null;
  if (value === null) return null;

  if (operator === 'in_last_days' || operator === 'not_in_last_days') {
    const days = Number(value);
    if (!Number.isInteger(days) || days <= 0) return null;
    const inWindow = actual !== null && actual >= addDays(today, -days) && actual <= today;
    return operator === 'in_last_days' ? inWindow : !inWindow;
  }
  if (operator === 'between') {
    if (!Array.isArray(value) || value.length !== 2) return null;
    return actual !== null && actual >= value[0] && actual <= value[1];
  }
  const v = String(value);
  // ISO calendar dates compare correctly as strings.
  switch (operator) {
    case 'equals': return actual === v;
    case 'gt': return actual !== null && actual > v;
    case 'gte': return actual !== null && actual >= v;
    case 'lt': return actual !== null && actual < v;
    default: return null;
  }
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** `today` for {@link LoopFilterContext}: the UTC calendar date, as the server's `CURRENT_DATE` would be. */
export function utcToday(now: Date): string {
  return now.toISOString().slice(0, 10);
}

/** `?loopFilter=` / `?filters=` wire form: compact JSON. */
export function serializeLoopFilter(filter: LoopFilter): string {
  return JSON.stringify(filter);
}

/** The filter in `raw`, or null when it is not JSON of the right shape or names a field, operator or value loops do not take. */
export function parseValidLoopFilter(raw: string | null | undefined): LoopFilter | null {
  if (!raw) return null;
  const parsed = safeParseJson(ValidLoopFilterSchema, raw);
  return parsed.success ? parsed.data : null;
}
